import PropTypes from 'prop-types';
import React from 'react';
import {observer} from 'mobx-react';

import Icon from 'core/components/Icon.js';
import {DirectoryStoreContextV2} from 'core/stores/RootStore.js';
import {
    DIRECTORY_INTERNAL_LINKING,
    INTERNAL_LINK,
    INTERNAL_LINK_CTA,
} from 'core/constants.js';

export const SECTION_ICONS = {
    'nearby_suburbs': 'medicalCross',
    'gp_nearby': 'stethoscope',
    'faq': 'messageQuestionCircle',
    'learn_more': 'bookOpen',
};


export const HEADING = 'Helpful links';

const linkShape = PropTypes.shape({
    destinationUrl: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
});

const sectionShape = PropTypes.shape({
    cta: linkShape,
    links: PropTypes.arrayOf(linkShape),
    sectionKey: PropTypes.string,
    sectionTitle: PropTypes.string.isRequired,
});

export function LinkSection({cta, links, sectionKey, sectionTitle}) {
    if (!links?.length) {
        return null;
    }
    const icon = SECTION_ICONS[sectionKey];

    return (
        <div className="internal-linking-v2__section">
            {icon && (
                <div className="internal-linking-v2__icon" aria-hidden="true">
                    <Icon name={icon} />
                </div>
            )}
            <div className="internal-linking-v2__body">
                <h3 className="internal-linking-v2__title">{sectionTitle}</h3>
                <ul className="internal-linking-v2__list">
                    {links.map((link, index) => (
                        <li key={index}>
                            <a
                                className="internal-linking-v2__link"
                                data-ga-object-type={
                                    DIRECTORY_INTERNAL_LINKING
                                }
                                data-ga-selector={INTERNAL_LINK}
                                href={link.destinationUrl}
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>
                {cta && (
                    <a
                        className="internal-linking-v2__cta"
                        data-ga-object-type={DIRECTORY_INTERNAL_LINKING}
                        data-ga-selector={INTERNAL_LINK_CTA}
                        href={cta.destinationUrl}
                    >
                        {cta.label}
                        <span
                            aria-hidden="true"
                            className="internal-linking-v2__cta-icon"
                        >
                            <Icon name="arrowRight" />
                        </span>
                    </a>
                )}
            </div>
        </div>
    );
}

LinkSection.propTypes = {
    cta: linkShape,
    links: PropTypes.arrayOf(linkShape),
    sectionKey: PropTypes.string,
    sectionTitle: PropTypes.string.isRequired,
};

LinkSection.defaultProps = {
    cta: null,
    links: [],
    sectionKey: '',
};


export function InternalLinkingBlock({sections}) {
    const populated = sections.filter((section) => section.links?.length);

    if (!populated.length) {
        return null;
    }

    return (
        <section
            aria-labelledby="internal-linking-heading"
            className="internal-linking-v2"
            data-testid="internal-linking-v2"
        >
            <div className="internal-linking-v2__content">
                <h2
                    className="internal-linking-v2__heading"
                    id="internal-linking-heading"
                >
                    {HEADING}
                </h2>
                <div className="internal-linking-v2__grid">
                    {populated.map((section) => (
                        <LinkSection key={section.sectionKey} {...section} />
                    ))}
                </div>
            </div>
        </section>
    );
}

InternalLinkingBlock.propTypes = {
    sections: PropTypes.arrayOf(sectionShape),
};

InternalLinkingBlock.defaultProps = {
    sections: [],
};

export default
@observer
class InternalLinkingV2 extends React.Component {
    static contextType = DirectoryStoreContextV2;

    render() {
        const sections = this.context.internalLinkingSections?.slice() ?? [];

        return <InternalLinkingBlock sections={sections} />;
    }
}
